import { Link } from "gatsby"
import React from "react"
import styled, { css } from "styled-components"

import { Arrow } from "../../assets/svg"
import { CoverButton } from "../../elements"
import { CoverType } from "../../elements/CoverButton"

import Article, { ArticleProps } from "../Article"

interface Props extends ArticleProps {
  color: string
  backArrow?: boolean
  coverType?: CoverType
  button?: React.ReactElement
  showButton?: boolean
}

const ProductArticle: React.FC<Props> = ({
  color,
  children,
  coverType,
  className,
  backArrow = true,
  button = null,
  showButton = true,
  ...props
}) => {
  return (
    <Container color={color} className={className}>
      <Article className="article" {...props}>
        {backArrow && (
          <BackButton to="/products/" arrowColor={color}>
            <Arrow />
            <span>back to products</span>
          </BackButton>
        )}
        {children}
        <Footer className="articleFooter">
          {showButton ??
            (button ? (
              button
            ) : (
              <CoverButton
                coverType={coverType}
                filled
                buttonColor={color}
                textColor="#fff"
              />
            ))}
        </Footer>
      </Article>
    </Container>
  )
}

export default ProductArticle

const Container = styled.div<{ color: string }>`
  padding-bottom: 60px;

  .article {
    gap: 100px;

    h1,
    h2 {
      background-color: ${props => props.color};
      color: #fff;
      font-size: 28px;
      line-height: 40px;
      max-width: 100vw;
      padding: 46px 20px;
    }

    p {
      padding-left: 20px;
      padding-right: 20px;
    }

    .coverButton {
      margin: 40px 20px;
    }

    @media screen and (min-width: ${props => props.theme.breakpoints.xs}) {
      h1 {
        font-size: 48px;
        line-height: 62px;
      }

      .coverButton {
        margin: 0 auto;
      }
    }

    @media screen and (min-width: ${props => props.theme.breakpoints.sm}) {
      padding-bottom: 0;

      h1,
      h2 {
        background-color: ${props => props.color};
        color: #fff;
        padding: 46px 80px;
        font-size: 48px;
        line-height: 62px;
      }

      p {
        font-size: 30px;
        line-height: 48px;
        padding-left: 80px;
        padding-right: 80px;
      }

      .coverButton {
        justify-self: flex-end;
        margin: 90px 0 70px auto;
      }
    }

    @media screen and (min-width: ${props => props.theme.breakpoints.md}) {
      h1,
      h2 {
        font-size: 52px;
        line-height: 72px;
      }

      .coverButton {
        margin-right: 0px;
      }
    }

    @media screen and (min-width: ${props => props.theme.breakpoints.lg}) {
      grid-template-columns: minmax(500px, 580px) 1fr;

      h1,
      h2,
      p {
        padding-left: 112px;
      }

      p {
        font-size: 30px;
        line-height: 48px;
      }
    }
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.md}) {
    padding: 0 60px 0 0;
  }
`

const BackButton = styled(Link)<{ arrowColor: string }>`
  align-items: center;
  background-color: transparent;
  border: none;
  display: flex;
  gap: 24px;
  grid-row: 1;
  margin-left: 20px;

  svg {
    transform: rotateZ(180deg);
    width: 25px;

    path {
      ${({ arrowColor }) => css`
        fill: ${arrowColor};
      `}
    }
  }

  span {
    font-size: 18px;
    line-height: 18px;
    margin-top: 4px;
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.sm}) {
    margin-left: 60px;
  }
`

const Footer = styled.div`
  padding: 0 20px;

  @media screen and (min-width: ${props => props.theme.breakpoints.sm}) {
    padding: 0 80px;
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.md}) {
    padding: 0;
  }
`
